import type { UsageHistoryEntry } from '@xhubio-saas/core-frontend/plugin-usage'

/**
 * How many series a usage chart keeps by name. Everything past it folds into ONE „Other" series.
 *
 * 🔴 Four, and the fold already happens at five series — not „five may stay on their own". The ring beside the
 * dashboard chart (core `TodayUsageCard`, `MAX_RINGS = 4`) shows the top four plus „Other" as soon as there is a
 * fifth type, and core is not ours to change here. Only the ring's rule lets ring, dashboard bars and billing bars
 * name and colour the same series the same way. A lone fifth series would also gain nothing: `--chart-5` is the
 * same grey as `--chart-other` (index.css) — one-hue palette, four accent steps.
 */
export const NAMED_SERIES_LIMIT = 4

/** Rank 1–4 → the accent ramp `--chart-1..4` (largest = strongest); the folded remainder is grey. */
const RANK_COLORS = ['var(--chart-1)', 'var(--chart-2)', 'var(--chart-3)', 'var(--chart-4)']
export const OTHER_SERIES_COLOR = 'var(--chart-other)'

export interface FoldedUsageSeries {
  /** Same buckets as the input, breakdown reduced to the named series (+ „Other"), rank order, zeros explicit. */
  data: UsageHistoryEntry[]
  /** Breakdown label → colour, for `UsageChart`'s `seriesColors`. */
  seriesColors: Record<string, string>
  /** The kept series, largest first; `otherLabel` last when something was folded. */
  rankedLabels: string[]
}

/**
 * Fold a stacked usage history (one entry per bucket, `breakdown` keyed by display label) to the four largest series
 * plus „Other" — ONE rule for every usage chart in the console (dashboard ring + bars, billing „Usage" tab).
 *
 * - Rank = the series' sum over the whole input (the chosen range), not per bucket.
 * - Ties break by label in code-point order, so the colour does not depend on the order the backend listed them.
 * - Ranked on the display label, not on the raw id: two ids with one name are one series in the chart anyway.
 * - Every bucket lists the kept labels in rank order (0 where absent), „Other" last: `UsageChart` takes legend,
 *   stack and tooltip order from the first breakdown it sees, so this is what puts rank 1 at the bottom of the
 *   stack and first in legend and tooltip.
 * - Bucket totals (`count`) stay as they were — folding moves calls between series, it never drops one.
 */
export function foldUsageSeries(
  data: UsageHistoryEntry[],
  otherLabel: string,
  limit: number = NAMED_SERIES_LIMIT
): FoldedUsageSeries {
  const totals = new Map<string, number>()
  for (const entry of data) {
    for (const [label, count] of Object.entries(entry.breakdown ?? {})) {
      totals.set(label, (totals.get(label) ?? 0) + count)
    }
  }
  const ranked = [...totals.entries()]
    .filter(([, total]) => total > 0)
    .sort(([labelA, totalA], [labelB, totalB]) =>
      totalB !== totalA ? totalB - totalA : labelA < labelB ? -1 : labelA > labelB ? 1 : 0
    )
    .map(([label]) => label)

  const named = ranked.length > limit ? ranked.slice(0, limit) : ranked
  const hasOther = ranked.length > named.length

  const seriesColors: Record<string, string> = {}
  named.forEach((label, rank) => {
    seriesColors[label] = RANK_COLORS[rank] ?? OTHER_SERIES_COLOR
  })
  if (hasOther) seriesColors[otherLabel] = OTHER_SERIES_COLOR

  const namedSet = new Set(named)
  const folded = data.map((entry) => {
    if (!entry.breakdown) return entry
    const breakdown: Record<string, number> = {}
    for (const label of named) breakdown[label] = entry.breakdown[label] ?? 0
    if (hasOther) {
      let rest = 0
      for (const [label, count] of Object.entries(entry.breakdown)) {
        if (!namedSet.has(label)) rest += count
      }
      breakdown[otherLabel] = rest
    }
    return { ...entry, breakdown }
  })

  return {
    data: folded,
    seriesColors,
    rankedLabels: hasOther ? [...named, otherLabel] : named,
  }
}
